import {Injector, ModuleWithProviders, NgModule} from '@angular/core';
import {CommonModule, DecimalPipe} from '@angular/common';
import {ApiResponseSummaryComponent} from './api-response/api-response-summary.component';
import {ContentEditableDirective} from './directives/contenteditable.directive';
import {SortByPipe} from './pipes/sort-by.pipe';
import {NgControlOptionsDirective} from './directives/ngControlOptions.directive';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {MesHttpInterceptor} from './mes-http.interceptor';
import {MesHttpStatusHandlerService} from './mes-http-status-handler.service';
import {AppConfig} from './app.config';
import {AutoFocusDirective} from './directives/auto-focus.directive';
import {LeModalService} from './modal/le-modal.service';
import {FindPipe} from './pipes/find.pipe';
import {MemoizePipe} from './pipes/memoize.pipe';
import {NgForTrackByIdDirective} from './directives/ng-for-track-by-id.directive';
import {InputDisableAutocompleteDirective} from './directives/input-disable-autocomplete.directive';
import {DisplayNamePipe} from "./pipes/display-name.pipe";
import {DateTimePickerComponent} from './date-time-picker/date-time-picker.component';
import {
  NgbDatepickerI18n,
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbModalModule,
  NgbPopoverModule,
  NgbTimepickerModule
} from "@ng-bootstrap/ng-bootstrap";
import {FormsModule} from "@angular/forms";
import {UnitNumberPipe} from "./pipes/unit-number.pipe";
import {UnitPipe} from './pipes/unit.pipe';
import {ConfirmationDialogComponent} from './confirmation-dialog/confirmation-dialog.component';
import {NumberInputDirective, NumbersOnly} from './directives/number-input.directive';
import {EnsureFullScreenDirective} from './directives/ensure-full-screen.directive';
import {NoSpacesPipe} from './pipes/no-spaces.pipe';
import {WherePipe} from './pipes/where.pipe';
import {DistinctPipe} from './pipes/distinct.pipe';
import {IsDefinedPipe} from './pipes/is-defined.pipe';
import {NgNumberInputDirective} from './directives/ng-number-input.directive';
import {ApiResponseComponent} from './api-response/api-response.component';
import {FilterByPipe, GroupByPipe, NgArrayPipesModule, NgStringPipesModule, OrderByPipe, ValuesPipe} from 'ngx-pipes';
import {LetDirective} from './directives/let.directive';
import {ClickOnceDirective} from './directives/click-once.directive';
import {setAppInjector} from "./helpers/general-helpers";
import {InlineSVGModule} from "ng-inline-svg-2";
import {TRANSLOCO_LOADER, TranslocoDirective, TranslocoModule} from "@jsverse/transloco";
import {GroupByValuesPipe} from './pipes/group-by-values.pipe';
import {NgbdDatepickerRangeComponent} from "./date-range-picker/ngbd-datepicker-range/ngbd-datepicker-range.component";
import {DateRangePickerComponent} from './date-range-picker/date-range-picker/date-range-picker.component';
import {NgxModalDraggableDirective} from './directives/ngx-modal-draggable.directive';
import {ChangelogComponent} from './changelog/changelog.component';
import {StylesheetComponent} from './stylesheet/stylesheet.component';
import {PagerPipe} from './pipes/pager.pipe';
import {SafeNumberPipe} from './pipes/safe-number.pipe';
import {DigitInfoByNumberPipe} from "./pipes/digit-info-by-number.pipe";
import {PrecisionByNumberPipe} from "./pipes/precision-by-number.pipe";
import {StopClickPropagationDirective} from './directives/stop-click-propagation.directive';
import {StopArrowKeyPropagationDirective} from './directives/stop-arrow-key-propagation.directive';
import {ScrollToTopComponent} from './scroll-to-top/scroll-to-top.component';
import {NumberFixedPipe} from './pipes/number-fixed.pipe';
import {SafePipe} from './pipes/safe.pipe';
import {JsonParsePipe, JsonPrettyPipe} from "./pipes/json-parse.pipe";
import {ScrollPanelModule} from "primeng/scrollpanel";
import {AddDaysPipe} from './pipes/add-days.pipe';
import {DateDiffPipe} from "./pipes/date-diff.pipe";
import {AveragePipe} from "./pipes/average.pipe";
import {WhereNotPipe} from "./pipes/where-not.pipe";
import {AssetImageUrlPipe} from './pipes/asset-image-url.pipe';
import {EditableModule} from "./edit-in-place/editable.module";
import {AutosizeDirective} from "./directives/autosize.directive";
import {DecimalPlacesPipe} from './pipes/decimal-places.pipe';
import {DateTimeFormatRoundingPipe} from './pipes/date-time-format-rounding.pipe';
import {LoadingSpinnerComponent} from "./loading-spinner/loading-spinner.component";
import {StopDragPropagationDirective} from './directives/stop-drag-propagation.directive';
import {TranslocoHttpLoader} from "./system/languages/base-transloco-with-key.service";
import {GlobalStatusHandlingComponent} from './global-status-handling/global-status-handling.component';
import {EditorModule} from "@tinymce/tinymce-angular";
import {RouterModule} from "@angular/router";
import {TimeSpanPipe} from './pipes/time-span.pipe';
import {ColumnFilterFieldConstraintsViewComponent} from "./table/column-filter-field-constraints-view/column-filter-field-constraints-view.component";
import {ToFileLinkModelPipe} from "./pipes/to-file-link-model.pipe";
import {MediaTypePipe} from './pipes/media-type.pipe';
import {UserPipe} from './pipes/user.pipe';
import {DurationTimerComponent} from './duration-timer/duration-timer.component';
import {NotificationStatusColorPipe} from "./notification-area/notification-status-color.pipe";
import {ChangeDetectionLoggingComponent} from "./change-detection-logging/change-detection-logging.component";
import {TranslocoDatepickerI18n} from "./system/languages/transloco-datepicker-i18n.service";
import {AsTypePipe} from "./pipes/as-type.pipe";
import {LeTranslocoDirective, LocalizedValuePipe} from "./system/languages/localized-value.pipe";

@NgModule({
  exports: [
    ApiResponseSummaryComponent,
    ContentEditableDirective,
    NgForTrackByIdDirective,
    FindPipe,
    SortByPipe,
    MemoizePipe,
    NgControlOptionsDirective,
    AutoFocusDirective,
    InputDisableAutocompleteDirective,
    DisplayNamePipe,
    DateTimePickerComponent,
    UnitNumberPipe,
    UnitPipe,
    ConfirmationDialogComponent,
    NumberInputDirective,
    NumbersOnly,
    EnsureFullScreenDirective,
    NoSpacesPipe,
    WherePipe,
    WhereNotPipe,
    DistinctPipe,
    IsDefinedPipe,
    NgNumberInputDirective,
    LetDirective,
    ClickOnceDirective,
    GroupByValuesPipe,
    NgbdDatepickerRangeComponent,
    NgxModalDraggableDirective,
    DateRangePickerComponent,
    ChangelogComponent,
    StylesheetComponent,
    SafeNumberPipe,
    DigitInfoByNumberPipe,
    PrecisionByNumberPipe,
    PagerPipe,
    StopClickPropagationDirective,
    StopDragPropagationDirective,
    StopArrowKeyPropagationDirective,
    ScrollToTopComponent,
    NumberFixedPipe,
    SafePipe,
    JsonParsePipe,
    AddDaysPipe,
    DateDiffPipe,
    AveragePipe,
    AssetImageUrlPipe,
    DecimalPlacesPipe,
    AutosizeDirective,
    DateTimeFormatRoundingPipe,
    LoadingSpinnerComponent,
    GlobalStatusHandlingComponent,
    TimeSpanPipe,
    ColumnFilterFieldConstraintsViewComponent,
    ToFileLinkModelPipe,
    MediaTypePipe,
    UserPipe,
    DurationTimerComponent,
    NotificationStatusColorPipe,
    AsTypePipe,
    LocalizedValuePipe,
    LeTranslocoDirective,
    JsonPrettyPipe
  ],
  declarations: [
    ApiResponseSummaryComponent,
    ContentEditableDirective,
    NgForTrackByIdDirective,
    SortByPipe,
    MemoizePipe,
    NgControlOptionsDirective,
    AutoFocusDirective,
    InputDisableAutocompleteDirective,
    DisplayNamePipe,
    DateTimePickerComponent,
    UnitNumberPipe,
    UnitPipe,
    ConfirmationDialogComponent,
    NumberInputDirective,
    NumbersOnly,
    EnsureFullScreenDirective,
    NoSpacesPipe,
    DistinctPipe,
    IsDefinedPipe,
    NgNumberInputDirective,
    ApiResponseComponent,
    LetDirective,
    ClickOnceDirective,
    GroupByValuesPipe,
    NgbdDatepickerRangeComponent,
    NgxModalDraggableDirective,
    DateRangePickerComponent,
    ChangelogComponent,
    StylesheetComponent,
    SafeNumberPipe,
    DigitInfoByNumberPipe,
    PrecisionByNumberPipe,
    PagerPipe,
    StopClickPropagationDirective,
    StopArrowKeyPropagationDirective,
    ScrollToTopComponent,
    NumberFixedPipe,
    SafePipe,
    JsonParsePipe,
    AddDaysPipe,
    DateDiffPipe,
    AveragePipe,
    AssetImageUrlPipe,
    AutosizeDirective,
    DecimalPlacesPipe,
    DateTimeFormatRoundingPipe,
    LoadingSpinnerComponent,
    StopDragPropagationDirective,
    GlobalStatusHandlingComponent,
    TimeSpanPipe,
    ColumnFilterFieldConstraintsViewComponent,
    ToFileLinkModelPipe,
    MediaTypePipe,
    UserPipe,
    DurationTimerComponent,
    NotificationStatusColorPipe
  ],
  imports: [
    CommonModule,
    NgbPopoverModule,
    FormsModule,
    NgbDatepickerModule,
    NgbTimepickerModule,
    NgArrayPipesModule,
    NgStringPipesModule,
    InlineSVGModule,
    NgbDropdownModule,
    TranslocoModule,
    NgbModalModule,
    ScrollPanelModule,
    EditableModule,
    EditorModule,
    RouterModule,
    AsTypePipe,
    LocalizedValuePipe,
    LeTranslocoDirective,
    JsonPrettyPipe,
    WherePipe,
    WhereNotPipe,
    FindPipe
  ],
  providers: [
    DecimalPipe,
    FindPipe,
    SortByPipe,
    MemoizePipe,
    LeModalService,
    DisplayNamePipe,
    UnitNumberPipe,
    UnitPipe,
    NoSpacesPipe,
    WherePipe,
    WhereNotPipe,
    DistinctPipe,
    IsDefinedPipe,
    OrderByPipe,
    FilterByPipe,
    GroupByValuesPipe,
    GroupByPipe,
    ValuesPipe,
    SafeNumberPipe,
    DigitInfoByNumberPipe,
    PrecisionByNumberPipe,
    PagerPipe,
    SafePipe,
    JsonParsePipe,
    DateDiffPipe,
    AveragePipe,
    AssetImageUrlPipe,
    DecimalPlacesPipe,
    DateTimeFormatRoundingPipe,
    TimeSpanPipe,
    provideHttpClient(withInterceptorsFromDi()),
    ChangeDetectionLoggingComponent
  ]
})

export class CommonSharedModule {
  constructor(injector: Injector) {
    setAppInjector(injector);
  }

  static forRoot(config: AppConfig): ModuleWithProviders<CommonSharedModule> {
    return {
      ngModule: CommonSharedModule,
      providers: [
        MesHttpStatusHandlerService,
        {provide: 'config', useValue: new AppConfig(config)},
        {provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader},
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MesHttpInterceptor,
          multi: true
        },
        {provide: NgbDatepickerI18n, useClass: TranslocoDatepickerI18n}
      ]
    };
  }
}
